import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { costCentersService } from '../../services/costCenters.service';
import {
  CostCenterDto,
  CreateCostCenterDto,
  UpdateCostCenterDto
} from '../../types/costCenters.types';

interface CostCenterDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  costCenter?: CostCenterDto | null;
}

const CostCenterDialog: React.FC<CostCenterDialogProps> = ({
  open,
  onClose,
  onSuccess,
  costCenter
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [formData, setFormData] = useState({
    costCenterCode: '',
    costCenterName: '',
    costCenterNameAr: '',
    parentCostCenterId: null as number | null,
    isFinalCostCenter: true,
    isActive: true,
    notes: ''
  });

  const [allCostCenters, setAllCostCenters] = useState<CostCenterDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadCostCenters();
      if (costCenter) {
        // Edit mode - load existing data
        
        setFormData({
          costCenterCode: costCenter.costCenterCode,
          costCenterName: costCenter.costCenterName,
          costCenterNameAr: costCenter.costCenterNameAr,
          parentCostCenterId: costCenter.parentCostCenterId || null,
          isFinalCostCenter: costCenter.isFinalCostCenter,
          isActive: costCenter.isActive,
          notes: costCenter.notes || ''
        });
        
      } else {
        // Create mode - generate next code automatically
        setFormData({
          costCenterCode: '',
          costCenterName: '',
          costCenterNameAr: '',
          parentCostCenterId: null,
          isFinalCostCenter: true,
          isActive: true,
          notes: ''
        });
        generateNextCode(null);
      }
      setError(null);
    }
  }, [open, costCenter]);

  const loadCostCenters = async () => {
    try {
      setLoading(true);
      const data = await costCentersService.getAll(true, false);
      // Filter out Final Cost Centers - they cannot be parents
      const nonFinalCostCenters = data.filter(cc => !cc.isFinalCostCenter);
      setAllCostCenters(nonFinalCostCenters);
    } catch (err) {
      console.error('Failed to load cost centers:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateNextCode = async (parentCode: string | null) => {
    try {
      setLoadingCode(true);
      const nextCode = await costCentersService.getNextCode(parentCode || undefined);
      setFormData(prev => ({ ...prev, costCenterCode: nextCode }));
    } catch (err: any) {
      console.error('Failed to generate next code:', err);
      const errorMsg = isRTL ? 'فشل توليد الرمز التلقائي' : 'Failed to generate code';
      setError(err.message || errorMsg);
      setFormData(prev => ({ ...prev, costCenterCode: parentCode ? `${parentCode}-0001` : 'CC-001' }));
    } finally {
      setLoadingCode(false);
    }
  };

  const handleParentChange = (newParent: CostCenterDto | null) => {
    setFormData(prev => ({
      ...prev,
      parentCostCenterId: newParent?.costCenterId || null
    }));

    // Regenerate code based on new parent
    if (!costCenter) { // Only in create mode
      generateNextCode(newParent?.costCenterCode || null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    try {
      setSaving(true);
      setError(null);

      if (costCenter) {
        // Update
        const updateData: UpdateCostCenterDto = {
          costCenterId: costCenter.costCenterId,
          costCenterCode: formData.costCenterCode,
          costCenterName: formData.costCenterName,
          costCenterNameAr: formData.costCenterNameAr,
          parentCostCenterId: formData.parentCostCenterId || undefined,
          isFinalCostCenter: formData.isFinalCostCenter,
          isActive: formData.isActive,
          notes: formData.notes || undefined
        };
        await costCentersService.update(costCenter.costCenterId, updateData);
      } else {
        // Create
        const createData: CreateCostCenterDto = {
          costCenterCode: formData.costCenterCode,
          costCenterName: formData.costCenterName,
          costCenterNameAr: formData.costCenterNameAr,
          parentCostCenterId: formData.parentCostCenterId || undefined,
          isFinalCostCenter: formData.isFinalCostCenter,
          notes: formData.notes || undefined
        };
        await costCentersService.create(createData);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMsg = isRTL ? 'فشل حفظ مركز التكلفة' : 'Failed to save cost center';
      setError(err.response?.data?.message || errorMsg);
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectedParent = allCostCenters.find(
    cc => cc.costCenterId === formData.parentCostCenterId
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {costCenter 
            ? (isRTL ? 'تعديل مركز تكلفة' : 'Edit Cost Center')
            : (isRTL ? 'إضافة مركز تكلفة' : 'Create Cost Center')
          }
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Parent Cost Center - FIRST */}
            <Autocomplete
              fullWidth
              options={allCostCenters}
              getOptionLabel={(option) => 
                isRTL 
                  ? `${option.costCenterCode} - ${option.costCenterNameAr}` 
                  : `${option.costCenterCode} - ${option.costCenterName}`
              }
              value={selectedParent || null}
              onChange={(_, newValue) => handleParentChange(newValue)}
              disabled={saving || loading || !!costCenter}
              loading={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isRTL ? 'مركز التكلفة الأب' : 'Parent Cost Center'}
                  helperText={isRTL 
                    ? 'الرمز يتولد تلقائياً بناءً على الأب'
                    : 'Code auto-generates based on parent'
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Code - Auto-generated based on parent */}
            <TextField
              required
              fullWidth
              label={isRTL ? 'الرمز' : 'Code'}
              value={formData.costCenterCode}
              onChange={(e) => setFormData({ ...formData, costCenterCode: e.target.value })}
              disabled={saving || loadingCode}
              placeholder={selectedParent ? `${selectedParent.costCenterCode}-0001` : 'CC-001'}
              helperText={isRTL 
                ? 'الرمز يُنشأ تلقائياً، يمكنك تعديله' 
                : 'Auto-generated, you can edit it'
              }
              InputProps={{
                endAdornment: loadingCode && <CircularProgress size={20} />
              }}
            />

            {/* Name */}
            <TextField
              required
              fullWidth
              label={isRTL ? 'الاسم' : 'Name'}
              value={formData.costCenterName}
              onChange={(e) => setFormData({ ...formData, costCenterName: e.target.value })}
              disabled={saving}
            />

            {/* Name Arabic */}
            <TextField
              required
              fullWidth
              label={isRTL ? 'الاسم بالعربي' : 'Arabic Name'}
              value={formData.costCenterNameAr}
              onChange={(e) => setFormData({ ...formData, costCenterNameAr: e.target.value })}
              disabled={saving}
              dir="rtl"
            />

            {/* Notes */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={isRTL ? 'ملاحظات' : 'Notes'}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={saving}
            />

            {/* Final Cost Center */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isFinalCostCenter}
                  onChange={(e) => setFormData({ ...formData, isFinalCostCenter: e.target.checked })}
                  disabled={saving}
                />
              }
              label={isRTL ? 'مركز تكلفة نهائي' : 'Final Cost Center'}
            />

            {/* Active (Edit only) */}
            {costCenter && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    disabled={saving}
                  />
                }
                label={isRTL ? 'نشط' : 'Active'}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={saving || loadingCode}
          >
            {saving 
              ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
              : (isRTL ? 'حفظ' : 'Save')
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CostCenterDialog;